import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/adsense-test")({
  head: () => ({
    meta: [
      { title: "AdSense Test — legaldecoder" },
      {
        name: "description",
        content: "Internal test page to verify AdSense script load and ad request behavior.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdSenseTestPage,
});

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

const PUBLISHER_ID = "ca-pub-3575836905623360";

function AdSenseTestPage() {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [adsbyGoogleAvailable, setAdsbyGoogleAvailable] = useState(false);
  const [pushed, setPushed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if the AdSense script tag is present
    const tag = document.querySelector(
      'script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]',
    );
    setScriptLoaded(Boolean(tag));

    // Poll briefly for window.adsbygoogle (script loads async)
    let tries = 0;
    const interval = window.setInterval(() => {
      tries += 1;
      if (window.adsbygoogle) {
        setAdsbyGoogleAvailable(true);
        window.clearInterval(interval);
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          setPushed(true);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } else if (tries > 30) {
        window.clearInterval(interval);
      }
    }, 200);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <main className="mx-auto max-w-3xl px-6 py-12">
      <h1 className="text-3xl font-bold text-foreground">AdSense Diagnostics</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Internal test page. Verifies the Google AdSense loader script is present, that{" "}
        <code>window.adsbygoogle</code> is initialized, and renders a responsive ad unit so you can
        observe the network request.
      </p>

      <section className="mt-6 rounded-2xl border bg-card p-5 text-card-foreground">
        <h2 className="text-lg font-semibold">Status</h2>
        <ul className="mt-3 space-y-2 text-sm">
          <li>
            Script tag in DOM:{" "}
            <strong className={scriptLoaded ? "text-green-600" : "text-red-600"}>
              {scriptLoaded ? "yes" : "no"}
            </strong>
          </li>
          <li>
            <code>window.adsbygoogle</code> ready:{" "}
            <strong className={adsbyGoogleAvailable ? "text-green-600" : "text-red-600"}>
              {adsbyGoogleAvailable ? "yes" : "no (still loading or blocked)"}
            </strong>
          </li>
          <li>
            Ad slot push attempted:{" "}
            <strong className={pushed ? "text-green-600" : "text-muted-foreground"}>
              {pushed ? "yes" : "pending"}
            </strong>
          </li>
          {error && <li className="text-red-600">Error: {error}</li>}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Note: a blank ad slot is normal on a brand-new site/page — AdSense may take 24–48h to
          begin serving ads even when the script and request are working correctly. The relevant
          signal is a network request to <code>pagead2.googlesyndication.com</code>.
        </p>
      </section>

      <section className="mt-6 rounded-2xl border bg-card p-5 text-card-foreground">
        <h2 className="text-lg font-semibold">Test ad unit</h2>
        <div className="mt-4 min-h-[120px]">
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client={PUBLISHER_ID}
            data-ad-slot="0000000000"
            data-ad-format="auto"
            data-full-width-responsive="true"
            data-adtest="on"
          />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          <code>data-adtest="on"</code> is set so this never counts as a real impression. Replace{" "}
          <code>data-ad-slot</code> with a real slot ID from your AdSense dashboard once you create
          ad units.
        </p>
      </section>

      <p className="mt-8 text-xs text-muted-foreground">
        NOT LEGAL ADVICE. FOR INFORMATIONAL PURPOSES ONLY.
      </p>
    </main>
  );
}
